import { belongsTo, Factory, Model } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from '@@/api/mock-server/server'
import { rand, randPastDate, randChanceBoolean } from '@ngneat/falso'
import { PaymentMethod } from '@/models/payments.js'
import { InvoicePaymentTransactionStatus } from '@/models/invoice.js'
import type { SchemaAttributes } from '@@/api/types'
import {
  filterable,
  pageable,
  PaginationError,
  createErrorResponse,
} from './utils.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'
import { includes as _includes } from 'lodash-es'
import { safeMathRandom, toNumber } from '@/utils/number.js'

type InvoicePaymentData = SchemaAttributes<'InvoicePayment'>

export const model = Model.extend({
  invoice: belongsTo('invoice'),
  paymentMethod: belongsTo('paymentMethod'),
  roundsPayment: belongsTo('invoicePayment'),
}) as ModelDefinition<InvoicePaymentData>

export const factory = Factory.extend<InvoicePaymentData>({
  invoice_id() {
    // This gets updated in the afterCreate hook based on the invoice relationship
    return 0
  },
  status() {
    return rand([
      InvoicePaymentTransactionStatus.Pending,
      InvoicePaymentTransactionStatus.Cancelled,
      InvoicePaymentTransactionStatus.Paid,
      InvoicePaymentTransactionStatus.Failed,
      null,
    ])
  },
  paid() {
    return 10
  },
  date_added() {
    return randPastDate().toISOString()
  },
  info() {
    return 'info text'
  },
  can_cancel() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  can_transfer_to_prepayments() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  refundable_amount() {
    return toNumber(this.paid)
  },
  allow_mass_cancel() {
    return true
  },
  afterCreate(invoicePayment, server) {
    const commonPaymentMethods = [
      PaymentMethod.CreditCard,
      PaymentMethod.Cash,
      PaymentMethod.Invoice,
    ]
    const otherPaymentMethods = Object.values(PaymentMethod).filter(
      (code) => !_includes(commonPaymentMethods, code),
    )

    const partialPaymentMethodCode =
      safeMathRandom() < 0.8
        ? // 80% chance for common payment methods
          rand(commonPaymentMethods)
        : // 20% chance for other payment methods
          rand(otherPaymentMethods)

    invoicePayment.update({
      paymentMethod: server.schema.findBy('paymentMethod', {
        code: partialPaymentMethodCode,
      }),
      invoice_id: toNumber(invoicePayment.invoice?.id),
    })
  },
})

export function routes(server: MockServer) {
  server.resource('invoicePayment', { path: '/invoice_payments' })

  // GET /invoice_payments - List invoice payments
  server.get(
    '/invoice_payments',
    function (this: MirageServer, schema, request) {
      const invoicePayments = schema.all('invoicePayment')

      try {
        // Apply filtering
        const filtered = filterable(invoicePayments, request, {
          invoice_id: (payment, value) => {
            return String(payment.invoiceId) === String(value)
          },
        })

        // Apply pagination
        const { data, meta } = pageable(filtered, request)

        return {
          ...this.serialize(data),
          meta: {
            pagination: meta,
          },
        }
      } catch (e) {
        if (e instanceof PaginationError) {
          return createErrorResponse({
            status: 404,
            errors: [{ detail: e.message }],
          })
        }
        return createErrorResponse({
          status: 500,
          errors: [{ detail: 'Internal Server Error' }],
        })
      }
    },
  )

  // GET /invoice_payments/:id - Retrieve specific invoice payment
  server.get(
    '/invoice_payments/:id',
    function (this: MirageServer, schema, request) {
      const { id } = request.params
      const payment = schema.find('invoicePayment', id)

      if (!payment) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Invoice payment with id ${id} not found` }],
        })
      }

      return payment
    },
  )

  // POST /invoice_payments - Create a new invoice payment
  server.post('/invoice_payments', function (schema, request) {
    const body = JSON.parse(request.requestBody || '{}')
    const attributes = body?.data?.attributes
    const relationships = body?.data?.relationships || {}

    if (!attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }

    // Get payment method from relationship
    const paymentMethodId = relationships?.payment_method?.data?.id
    let paymentMethod = null
    if (paymentMethodId) {
      paymentMethod = schema.find('paymentMethod', paymentMethodId)
      if (!paymentMethod) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Payment method not found' }],
        })
      }
    } else {
      paymentMethod = schema.first('paymentMethod')
    }

    // Get invoice from relationship if provided
    let invoice = null
    const invoiceId = relationships?.invoice?.data?.id
    if (invoiceId) {
      invoice = schema.find('invoice', invoiceId)
      if (!invoice) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Invoice not found' }],
        })
      }
    }

    // Create the invoice payment
    const invoicePayment = server.create('invoicePayment', {
      ...attributes,
      paymentMethod,
      invoice,
      status: attributes.status || InvoicePaymentTransactionStatus.Paid,
      paid: attributes.paid || 0,
      date_added: attributes.date_added || getTodayDateTimeISO(),
    })

    return invoicePayment
  })
}
